'use client';

import { useState } from 'react';

type Meta = {
  title: string;
  description: string;
  image: string;
  siteName: string;
  url: string;
};

const DEFAULT_META: Meta = {
  title: 'Your page title goes here',
  description:
    'A short, compelling description of the page. This is the copy people see under the title in a shared link.',
  image: '',
  siteName: 'example.com',
  url: 'https://example.com/article',
};

// Pull the hostname out of a URL string for the small domain label.
function domainOf(meta: Meta): string {
  const source = meta.url || meta.siteName;
  if (!source) return '';
  try {
    const normalized = source.startsWith('http') ? source : `https://${source}`;
    return new URL(normalized).hostname.replace(/^www\./, '');
  } catch {
    return source
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0];
  }
}

// Parse pasted <meta> HTML into meta values. DOMParser is only touched inside
// this handler, and guarded, so server rendering never reaches it.
function parseMetaHtml(html: string): Partial<Meta> | null {
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const pick = (selectors: string[]): string => {
    for (const selector of selectors) {
      const el = doc.querySelector(selector);
      const content = el?.getAttribute('content');
      if (content && content.trim()) return content.trim();
    }
    return '';
  };
  const titleTag = doc.querySelector('title')?.textContent?.trim() ?? '';
  const result: Partial<Meta> = {};
  const title = pick(['meta[property="og:title"]', 'meta[name="twitter:title"]']) || titleTag;
  const description = pick([
    'meta[property="og:description"]',
    'meta[name="twitter:description"]',
    'meta[name="description"]',
  ]);
  const image = pick(['meta[property="og:image"]', 'meta[name="twitter:image"]']);
  const siteName = pick(['meta[property="og:site_name"]']);
  const url = pick(['meta[property="og:url"]']);
  if (title) result.title = title;
  if (description) result.description = description;
  if (image) result.image = image;
  if (siteName) result.siteName = siteName;
  if (url) result.url = url;
  return result;
}

function ImageBox({ meta, height }: { meta: Meta; height: number }) {
  if (meta.image) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        alt="Social card preview"
        src={meta.image}
        style={{
          width: '100%',
          height,
          objectFit: 'cover',
          display: 'block',
          background: '#e2e8f0',
        }}
      />
    );
  }
  return (
    <div
      style={{
        width: '100%',
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #e2e8f0, #cbd5e1)',
        color: '#64748b',
        fontSize: '0.75rem',
        fontWeight: 600,
      }}
    >
      Image preview (add an image URL)
    </div>
  );
}

export default function BrowserOgPreview() {
  const [meta, setMeta] = useState<Meta>(DEFAULT_META);
  const [rawHtml, setRawHtml] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function setField(key: keyof Meta, value: string) {
    setMeta((prev) => ({ ...prev, [key]: value }));
  }

  function handleParse() {
    if (!rawHtml.trim()) {
      setError('Paste some <meta> HTML first.');
      return;
    }
    const parsed = parseMetaHtml(rawHtml);
    if (!parsed || Object.keys(parsed).length === 0) {
      setError('No recognizable meta tags found in that HTML.');
      setMessage(null);
      return;
    }
    setMeta((prev) => ({ ...prev, ...parsed }));
    setError(null);
    setMessage(`Filled ${Object.keys(parsed).length} field(s) from the pasted HTML.`);
  }

  const domain = domainOf(meta);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Preview</span>
        <h2 className="tool-live-title">Open Graph social preview</h2>
        <p className="tool-live-subtitle">
          Enter your title, description, image, and URL, or paste raw &lt;meta&gt; HTML to parse,
          then see how the link looks on Facebook, LinkedIn, X, Slack, and Discord. Previews use the
          values you provide, nothing is fetched from a server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>og:title</span>
              <input
                onChange={(e) => setField('title', e.target.value)}
                placeholder="Page title"
                type="text"
                value={meta.title}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>og:description</span>
              <textarea
                onChange={(e) => setField('description', e.target.value)}
                placeholder="Page description"
                rows={3}
                value={meta.description}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>og:image (URL)</span>
              <input
                onChange={(e) => setField('image', e.target.value)}
                placeholder="https://example.com/card.png"
                type="text"
                value={meta.image}
              />
            </label>
            <label className="tool-live-field">
              <span>og:site_name</span>
              <input
                onChange={(e) => setField('siteName', e.target.value)}
                placeholder="Example"
                type="text"
                value={meta.siteName}
              />
            </label>
            <label className="tool-live-field">
              <span>og:url</span>
              <input
                onChange={(e) => setField('url', e.target.value)}
                placeholder="https://example.com/article"
                type="text"
                value={meta.url}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Or paste raw &lt;meta&gt; HTML to parse</span>
              <textarea
                onChange={(e) => setRawHtml(e.target.value)}
                placeholder={
                  '<meta property="og:title" content="..." />\n<meta property="og:image" content="..." />'
                }
                rows={4}
                value={rawHtml}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleParse} type="button">
              Parse meta HTML
            </button>
          </div>
          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {message ? <p className="status-message status-success">{message}</p> : null}
          <p className="tool-live-panel-note">
            Previews are approximations. Each network renders cards slightly differently.
          </p>
        </section>

        <section className="tool-live-preview">
          <div style={{ display: 'grid', gap: '1.25rem', color: '#0f172a' }}>
            {/* Facebook / LinkedIn */}
            <div>
              <p className="tool-live-panel-note" style={{ marginTop: 0 }}>
                Facebook / LinkedIn
              </p>
              <div
                style={{
                  border: '1px solid #dfe1e6',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: '#fff',
                  maxWidth: '500px',
                }}
              >
                <ImageBox height={260} meta={meta} />
                <div style={{ padding: '0.65rem 0.75rem', background: '#f2f3f5' }}>
                  {domain ? (
                    <div
                      style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#606770' }}
                    >
                      {domain}
                    </div>
                  ) : null}
                  <div
                    style={{ fontSize: '1rem', fontWeight: 700, color: '#1d2129', lineHeight: 1.3 }}
                  >
                    {meta.title || 'Untitled'}
                  </div>
                  <div
                    style={{
                      fontSize: '0.82rem',
                      color: '#606770',
                      marginTop: '0.2rem',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {meta.description}
                  </div>
                </div>
              </div>
            </div>

            {/* X / Twitter */}
            <div>
              <p className="tool-live-panel-note" style={{ marginTop: 0 }}>
                X / Twitter (summary large image)
              </p>
              <div
                style={{
                  border: '1px solid #cfd9de',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  background: '#fff',
                  maxWidth: '500px',
                }}
              >
                <ImageBox height={250} meta={meta} />
                <div style={{ padding: '0.6rem 0.75rem' }}>
                  {domain ? (
                    <div style={{ fontSize: '0.82rem', color: '#536471' }}>{domain}</div>
                  ) : null}
                  <div
                    style={{
                      fontSize: '0.92rem',
                      fontWeight: 500,
                      color: '#0f1419',
                      lineHeight: 1.3,
                    }}
                  >
                    {meta.title || 'Untitled'}
                  </div>
                  <div
                    style={{
                      fontSize: '0.85rem',
                      color: '#536471',
                      marginTop: '0.15rem',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {meta.description}
                  </div>
                </div>
              </div>
            </div>

            {/* Slack / Discord */}
            <div>
              <p className="tool-live-panel-note" style={{ marginTop: 0 }}>
                Slack / Discord
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: '0.75rem',
                  borderLeft: '4px solid #4a154b',
                  paddingLeft: '0.75rem',
                  maxWidth: '500px',
                }}
              >
                <div style={{ flex: 1 }}>
                  {meta.siteName ? (
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1d1c1d' }}>
                      {meta.siteName}
                    </div>
                  ) : null}
                  <div
                    style={{
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      color: '#1264a3',
                      lineHeight: 1.3,
                    }}
                  >
                    {meta.title || 'Untitled'}
                  </div>
                  <div
                    style={{
                      fontSize: '0.85rem',
                      color: '#454245',
                      marginTop: '0.15rem',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {meta.description}
                  </div>
                  {meta.image ? (
                    <div style={{ marginTop: '0.5rem', maxWidth: '360px' }}>
                      <ImageBox height={180} meta={meta} />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <p className="tool-live-panel-note">Rendered locally from the values you provide.</p>
        </section>
      </div>
    </div>
  );
}
